import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons/faExclamationCircle";
import { Tooltip } from "../../../utils/local-antd";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { AtiraUpload } from "../../../components/AtiraUpload";
import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { Text } from "../../../components/Text";
import { PageMeta } from "../../../model/meta/PageMeta";
import { entryActions } from "../../../redux/entry/entry.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { EntriesImportTabConflicts } from "./EntriesImportTabConflicts";
import { Info } from "../../../components/Info";
import { useDrawersContext } from "../../tasks/drawers-context";
import type { UploadFile } from "../../../utils/local-antd";
import { AxiosError, isAxiosError } from "axios";
import { ImportEntriesDtoResponseDto } from "../../../model/entry/dto/ImportEntriesResponseDto";
import { TestIDs } from "../../../tests/types/testIds";

export const EntriesImportTab: React.FC = () => {
  const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null);
  const [laoding, setLoading] = useState(false);
  const [showConflictsModal, setShowConflictsModal] = useState(false);
  const [standardHeaders, setStandardHeaders] = useState<string[]>([]);
  const [userHeaders, setUserHeaders] = useState<string[]>([]);
  const [headersRowIndex, setHeadersRowIndex] = useState<string | undefined>(
    undefined,
  );

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const { setImportExportVisible } = useDrawersContext();

  const onImport = async () => {
    try {
      setLoading(true);
      await dispatch(
        entryActions.importEntries({
          userId,
          file: uploadedFile!,
          headersRowIndex,
        }),
      ).unwrap();

      AtiraToast.success(t("entries.import.conflict-modal.success"));
      setUploadedFile(null);
      dispatch(
        entryActions.getLatestEntries({ userId, meta: PageMeta.create() }),
      );
      setImportExportVisible(false);
    } catch (e: any) {
      if (
        e.response?.statusCode === 409 ||
        (e as unknown as AxiosError).response?.status === 409
      ) {
        const res = (
          isAxiosError(e) ? e.response?.data : e
        ) as ImportEntriesDtoResponseDto;
        AtiraToast.info(t("entries.import.conflict.info"), { duration: 15 });
        setShowConflictsModal(true);
        setStandardHeaders(res.standardHeaders);
        setUserHeaders(res.userHeaders);
      } else {
        AtiraToast.apiError(e);
      }
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex flexDirection="column" gap="m">
      <Info info={t("entries.import.title")} />
      <Info color="warning" info={t("entries.import.title-2")} />

      <hr />
      <Flex flexDirection="column" gap="xs">
        <Flex justifyContent="flex-start" alignItems="center" gap="xs">
          <Text fontSize="m">{t("entries.import.set-index.title")}</Text>
          <Tooltip
            styles={{ container: { whiteSpace: "pre-wrap" } }}
            title={t("entries.import.set-index.tooltip")}
          >
            <span />
            <AtiraIcon cursor="help" icon={faExclamationCircle} size="xs" />
          </Tooltip>
        </Flex>
        <Flex flexDirection="column" gap="s">
          <Input
            placeholder={t("entries.import.set-index.input.placeholder")}
            type="number"
            min={1}
            value={headersRowIndex}
            onChange={(e) => {
              setHeadersRowIndex(e?.target?.value);
            }}
          />
        </Flex>
      </Flex>

      <AtiraUpload
        maxCount={1}
        dragHover
        title={t("entries.import.upload.title")}
        onChange={(e) => setUploadedFile(e)}
        accept=".csv"
        fileList={uploadedFile ? [uploadedFile] : []}
      />

      <Button
        title={t("entries.import.modal.button")}
        loading={laoding}
        disabled={!uploadedFile}
        onClick={onImport}
        dataTestId={TestIDs.entries.import.startButton}
      />

      <EntriesImportTabConflicts
        open={showConflictsModal}
        onClose={() => setShowConflictsModal(false)}
        standardHeaders={standardHeaders}
        userHeaders={userHeaders}
        originalFile={uploadedFile!}
      />
    </Flex>
  );
};
